import { type FC } from "react";
import { useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";

import dancingRaccoonGif from "@app-assets/gif/dancing-raccoon.gif";

import { LensAutomationToolMaintenanceStatus, SurfaceType } from "../../../gql-generated/types";
import { CardTitle, PageContent, PageContentSpinner, PageHeader } from "../../../layout/components";
import { type GetLensQuery, type GetLensQueryVariables, getLens } from "../../../lens/api/queries";
import {
    type GetLensAutomationToolMaintenanceStatusQuery,
    type GetLensAutomationToolMaintenanceStatusQueryVariables,
    getLensAutomationToolMaintenanceStatus,
} from "../../../shared/api/queries";
import { Flexbox } from "../../../shared/components";
import { SearchParamKeys, useSearchParams } from "../../../shared/hooks";

import { BusinessManagerButton } from "./BusinessManagerButton";
import { ManageLensesButton } from "./ManageLensesButton";
import {
    bodyContainerStyles,
    businessManagerButton,
    successfulPageContent,
    successfulPageText,
} from "./SuccessfulSubmitPage.styles";
import {
    DelayMessage,
    SuccessCallToActionBase,
    SuccessCallToActionWithBM,
    SuccessCardMessage,
    SuccessCardTitle,
    SuccessPageHeader,
    TimeToAvailabilitySnapchatLens,
    TimeToAvailabilitySpectaclesLens,
} from "./messages.generated";
import { surfaceTypeCacheModify } from "./surfaceTypeCacheModify";

interface SuccessfulSubmitPage {
    showDelayMessage: boolean;
    isSpectaclesOnly: boolean;
}

const SuccessfulSubmitPage: FC<SuccessfulSubmitPage> = ({ showDelayMessage, isSpectaclesOnly }) => {
    const delayMessage = showDelayMessage && <DelayMessage />;

    return (
        <PageContent data-testid="successful-submit">
            <PageHeader>
                <SuccessPageHeader />
            </PageHeader>
            <Flexbox flexDirection="column" alignItems="center" className={successfulPageContent}>
                <CardTitle>
                    <SuccessCardTitle />
                </CardTitle>
                <p className={bodyContainerStyles}>
                    <SuccessCardMessage
                        delayMessage={delayMessage}
                        timeTo={
                            isSpectaclesOnly ? <TimeToAvailabilitySpectaclesLens /> : <TimeToAvailabilitySnapchatLens />
                        }
                    />
                </p>
                <img alt="Colorful dancing cartoon raccoon with pool tube around belly" src={dancingRaccoonGif} />
                <div className={successfulPageText}>
                    <p>
                        <SuccessCallToActionBase withBM={isSpectaclesOnly ? "." : <SuccessCallToActionWithBM />} />
                    </p>
                </div>
                <div>
                    <ManageLensesButton type="default" />
                    {!isSpectaclesOnly && <BusinessManagerButton className={businessManagerButton} />}
                </div>
            </Flexbox>
        </PageContent>
    );
};

export const SuccessfulSubmitPageContainer: FC = () => {
    const { lensId = "" } = useParams();

    const searchParams = useSearchParams();
    const surfaceTypeFromSearch = searchParams.get(SearchParamKeys.SurfaceType);
    const { data, loading: latStatusLoading } = useQuery<
        GetLensAutomationToolMaintenanceStatusQuery,
        GetLensAutomationToolMaintenanceStatusQueryVariables
    >(getLensAutomationToolMaintenanceStatus);
    const { data: lensData, loading: lensLoading } = useQuery<GetLensQuery, GetLensQueryVariables>(getLens, {
        variables: { lensId },
        // Defensive: the route is `success/:lensId`, so today it always matches with an id — but
        // `useParams` still defaults to "", and that would be a doomed read.
        skip: lensId.length === 0,
        onCompleted: () => {
            surfaceTypeCacheModify(lensId, surfaceTypeFromSearch);
        },
    });

    if (latStatusLoading || lensLoading) {
        return <PageContentSpinner />;
    }

    const surfaceType = lensData?.getLens.lens?.surfaceType ?? [SurfaceType.SurfaceTypeSnapchat];
    const isSpectaclesOnly = surfaceType.length === 1 && surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);
    const lensAutomationToolMaintenanceStatus = data?.getLensAutomationToolMaintenanceStatus.status;
    const showDelayMessage =
        lensAutomationToolMaintenanceStatus ===
        LensAutomationToolMaintenanceStatus.LensAutomationToolMaintenanceStatusOn;
    return <SuccessfulSubmitPage showDelayMessage={showDelayMessage} isSpectaclesOnly={isSpectaclesOnly} />;
};
